import {Button, Divider, Flex, Group, Modal, ModalProps, Stack, Text} from "@mantine/core";
import {adjustFromTimeZone, adjustToTimeZone, NoticeEntity} from "@ots-frontend/ots-data";
import {NoticeSeverityBadge} from "../../badge/NoticeSeverityBadge/NoticeSeverityBadge";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNoticeMutation} from "../../../hooks/useNoticeMutation";
import {DatePicker} from "../../date/DatePicker/DatePicker";

export type NoticeModalProps = ModalProps & {
  notice: NoticeEntity
}

export const NoticeModal = (props: NoticeModalProps) => {
  const {notice, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [_notice, _setNotice] = useState<NoticeEntity | undefined>(notice)

  const expired = useMemo(() => (_notice ? _notice.isExpired() : false), [_notice]);
  const canExpire = !expired;
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const {
    updateNotice,
    isUpdating,
  } = useNoticeMutation()

  useEffect(() => {
    if (_notice.expiresDttm) {
      const d = adjustFromTimeZone(_notice.expiresDttm);
      setExpiresAt(Number.isFinite(d.getTime()) ? d : null);
    } else {
      setExpiresAt(new Date(Date.now() + 5 * 60 * 1000)); // default "expire soon"
    }
  }, [_notice.expiresDttm]);

  const byline = useMemo(() => {
    let value = `${getFormattedDatabaseCode(_notice.category, t)} • ${getFormattedDateTime(_notice.createDttm)}`
    if (_notice.createdByName) {
      value += ` • ${_notice.createdByName}`
    }
    return value
  }, [_notice.category, _notice.createDttm, _notice.createdByName, t])

  const expiresLine = useMemo(() => {
    if (!_notice.expiresDttm) {
      return t<string>("common.modal.notice.noexpiration", "No expiration");
    }

    if (expired) {
      return `${t<string>("common.modal.notice.expired", "Expired")} • ${getFormattedDateTime(_notice.expiresDttm)}`;
    }

    return `${t<string>("common.modal.notice.expires", "Expires")} • ${getFormattedDateTime(_notice.expiresDttm)}`;
  }, [_notice.expiresDttm, expired, t]);

  const handleExpireNow = useCallback(() => {
    if (!_notice.expiresDttm) {
      updateNotice({
        id: _notice.id,
        dto: {
          expiresDttm: adjustToTimeZone().getTime(),
        },
      }, {
        onSuccess: (n) => _setNotice(NoticeEntity.from(n))
      })
    }
  }, [updateNotice, _notice.id, _notice.expiresDttm])

  const handleSetExpiration = useCallback(() => {
    if (!_notice.expiresDttm) {
      updateNotice({
        id: _notice.id,
        dto: {
          expiresDttm: adjustToTimeZone(expiresAt).getTime(),
        },
      }, {
        onSuccess: (n) => _setNotice(NoticeEntity.from(n))
      })
    }
  }, [updateNotice, _notice.id, _notice.expiresDttm, expiresAt])

  return (
    <Modal {...rest}>
      <Stack spacing="sm">
        <Flex align="flex-start" justify="space-between" gap="sm">
          <Stack spacing={2} style={{textAlign: "left", flex: 1}}>
            <Text fw={600} size="sm">
              {_notice.title}
            </Text>
            <Text size="xs" my="md">
              {_notice.bodyText}
            </Text>
            <Text size="xs" c="dimmed">
              {byline}
            </Text>
          </Stack>

          <NoticeSeverityBadge value={_notice.severity} variant="filled"/>
        </Flex>

        <Divider variant="dashed"/>

        <Group spacing="space-between" align="flex-end">
          <div>
            <Text fw={600} size="sm">
              {t("common.modal.notice.expiration", "Expiration")}
            </Text>
            <Text size="xs" c="dimmed">
              {expiresLine}
            </Text>
          </div>
        </Group>

        {!expired && (
          <Stack align="center">
            <DatePicker
              size="xs"
              value={expiresAt}
              onChange={setExpiresAt}
              minDate={new Date()}
            />

            <Group>
              <Button
                color="red"
                variant="subtle"
                onClick={handleExpireNow}
                disabled={!canExpire}
                loading={isUpdating}
              >
                {t("common.modal.notice.expirenow", "Expire now")}
              </Button>

              <Button
                variant="subtle"
                onClick={handleSetExpiration}
                disabled={!canExpire || !expiresAt}
                loading={isUpdating}
              >
                {t("common.modal.notice.saveexpiration", "Save expiration")}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
